import { useContext, useEffect, memo, useState, useMemo, useCallback, FC, ComponentType } from "react";

import {
  UserPermissions,
  Nullable,
  DashboardRoutes,
  getDefaultOrganizationRoute,
  getDefaultDonorRoute,
  browserDefaultTimezone,
} from "@simplyk/common";
import { useRouter } from "next/router";

import { useCurrentUserContext } from "../../contexts/CurrentUserContext";
import { useStripeAccountContext } from "../../contexts/StripeAccountContext";
import { redirectToLogout } from "../../helpers/auth";
import { trpc } from "../../helpers/trpc";
import { useGetIsFormReadyModalOpen } from "../../hooks/useGetIsFormReadyModalOpen";
import { useGtm } from "../../hooks/useGtm";
import { LayoutProps } from "../../layouts/type";
import { PageLoadingContext } from "../../providers/PageLoadingProvider";
import { DashboardMobileConnectBankPopup } from "../DashboardMobilePopup/DashboardMobileConnectBankPopup";
import { DashboardMobileShareFormPopup } from "../DashboardMobilePopup/DashboardMobileShareFormPopup";

import { setObjectInLocalStorage } from "@/helpers/localStorage";
import { useLogRouteChangePageVisits, RouteType } from "@/hooks/amplitude/useLogRouteChangePageVisits";
import { useRedirection } from "@/hooks/useRedirection";

interface OrganizationRouteProps extends LayoutProps {
  layout: ComponentType<LayoutProps>;
  permissions: Nullable<UserPermissions[]> | undefined;
  noScreenPadding?: boolean;
  styles?: { background: Record<string, unknown> };
}

const OrganizationRouteToMemoized: FC<OrganizationRouteProps> = ({
  children,
  layout: Layout,
  permissions,
  noScreenPadding,
  styles,
  ...restOfLayoutProps
}) => {
  const router = useRouter();
  const { pushGtmFirstPage } = useGtm();
  useLogRouteChangePageVisits({ path: router.pathname, routeType: RouteType.OrganizationRoute });
  const isFormReadyModalOpen = useGetIsFormReadyModalOpen();
  const { setRedirectionLoading } = useContext(PageLoadingContext);

  const { isStripeCustomAccountActive } = useStripeAccountContext();
  const { userPermissions, isAuthenticated, firstPaymentDate, currentUser, connectedAsAdministrator, organization } =
    useCurrentUserContext();

  const { hasForm, isLoading, shouldBeInDonorDashboard, shouldBeLoggedOut } = useRedirection();

  const { data: cardVolumeData, isLoading: cardVolumeLoading } = trpc.getCardVolume.useQuery(
    {
      organizationId: organization?.id!,
      timezone: browserDefaultTimezone,
    },
    {
      enabled: !shouldBeInDonorDashboard && !!organization?.id,
    }
  );

  // isRedirecting allow to know that a redirection is in progress, so we need to display a loader, avoid a glitch
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isRedirectionLoading = Boolean(isLoading || isRedirecting || shouldBeLoggedOut || shouldBeInDonorDashboard);
  useEffect(() => {
    setRedirectionLoading(isRedirectionLoading);
  }, [isRedirectionLoading, setRedirectionLoading]);

  const manageIsRedirecting = useCallback((redirection: () => Promise<boolean> | undefined) => {
    setIsRedirecting(true);
    const redirectionPromise = redirection();
    if (redirectionPromise) {
      redirectionPromise.finally(() => {
        setIsRedirecting(false);
      });
    } else {
      setIsRedirecting(false);
    }
  }, []);

  const redirectToRoute = useCallback(
    (route: DashboardRoutes) => {
      manageIsRedirecting(() => router.push(route));
    },
    [manageIsRedirecting, router]
  );

  useEffect(() => {
    if (currentUser?.email && !cardVolumeLoading && !connectedAsAdministrator) {
      pushGtmFirstPage({ email: currentUser?.email, paymentCount: cardVolumeData?.object?.count || 0 });
      // Used by marketing team to know if user has already an admin Zeffy account.
      setObjectInLocalStorage?.("zeffyAdminUser", {
        userId: currentUser?.id,
        paymentCount: cardVolumeData?.object?.count || 0,
      });
    }
  }, [
    connectedAsAdministrator,
    currentUser?.email,
    cardVolumeData?.object?.count,
    pushGtmFirstPage,
    cardVolumeLoading,
    currentUser?.id,
  ]);

  // First we check if the user should be logged out
  useEffect(() => {
    if (shouldBeLoggedOut) {
      const currentPath = router.asPath;
      manageIsRedirecting(() => redirectToLogout({ redirectionUrl: currentPath, connectedAsAdministrator }));
    }
  }, [manageIsRedirecting, shouldBeLoggedOut, router.asPath, connectedAsAdministrator]);

  // Second we check if the user should be redirected to the donor dashboard
  useEffect(() => {
    if (shouldBeInDonorDashboard) {
      redirectToRoute(getDefaultDonorRoute());
    }
  }, [manageIsRedirecting, redirectToRoute, router, shouldBeInDonorDashboard]);

  const hasPermission = useMemo(() => {
    return !!permissions && permissions.some((permission) => userPermissions.includes(permission));
  }, [permissions, userPermissions]);

  // Lastly, we check if the user has the permission to access the page
  useEffect(() => {
    if (!isLoading && isAuthenticated && permissions && !hasPermission) {
      redirectToRoute(getDefaultOrganizationRoute(userPermissions));
    }
  }, [isAuthenticated, permissions, userPermissions, hasPermission, isLoading, redirectToRoute]);

  const childComponent = (() => {
    if (isStripeCustomAccountActive && !firstPaymentDate) {
      return <DashboardMobileShareFormPopup>{children}</DashboardMobileShareFormPopup>;
    }
    return (
      <DashboardMobileConnectBankPopup
        isDisplayed={Boolean(hasForm && !isStripeCustomAccountActive && !isFormReadyModalOpen)}
      >
        {children}
      </DashboardMobileConnectBankPopup>
    );
  })();

  return (
    <Layout noScreenPadding={noScreenPadding} styles={styles} {...restOfLayoutProps}>
      {childComponent}
    </Layout>
  );
};

export const OrganizationRoute = memo(OrganizationRouteToMemoized);
